/** General Purpose Helpers */

import { notifications } from '@mantine/notifications';

/**
 * Show notification (toast)
 * @param {string} message - Message to display
 * @param {string} color - Color theme (blue, red, green, etc.)
 * @param {number} autoClose - Auto-close duration in ms
 */
export const showNotification = (message, color = 'blue', autoClose = 2500) => {
  notifications.show({ message, color, autoClose });
};

/**
 * Show error notification
 * @param {string} message - Error message
 */
export const showError = (message) => {
  showNotification(message, 'red');
};

/**
 * Show success notification
 * @param {string} message - Success message
 */
export const showSuccess = (message) => {
  showNotification(message, 'green');
};

/**
 * Show warning notification
 * @param {string} message - Warning message
 */
export const showWarning = (message) => {
  showNotification(message, 'yellow');
};

/**
 * Get value from localStorage with fallback
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Stored or default value
 */
export const getStorageValue = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Set value in localStorage
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 */
export const setStorageValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error('Storage error:', err);
  }
};

/**
 * Remove value from localStorage
 * @param {string} key - Storage key
 */
export const removeStorageValue = (key) => {
  localStorage.removeItem(key);
};

/**
 * Clear all localStorage
 */
export const clearAllStorage = () => {
  localStorage.clear();
};

/**
 * Convert FormData object to plain object
 * @param {FormData} formData - Form data
 * @returns {object} Plain object
 */
export const formDataToObject = (formData) => {
  const obj = {};
  for (const [key, value] of formData.entries()) {
    obj[key] = value;
  }
  return obj;
};

/**
 * Check if value is empty (null, undefined, empty string, empty array)
 * @param {*} value - Value to check
 * @returns {boolean}
 */
export const isEmpty = (value) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  );
};

/**
 * Debounce function to limit function calls
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit function call frequency
 * @param {function} func - Function to throttle
 * @param {number} limit - Time limit in ms
 * @returns {function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Deep clone object (simple version)
 * @param {*} obj - Object to clone
 * @returns {*} Cloned object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Merge objects deeply
 * @param {object} target - Target object
 * @param {object} source - Source object
 * @returns {object} Merged object
 */
export const deepMerge = (target, source) => {
  const result = { ...target };
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  return result;
};
